import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useContext } from 'react';

import { Ranking } from '@flipster/apis/v2/trading-competition/types';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import LockClosedIcon from '@flipster/react/icon/generated/LockClosedIcon';
import { css } from '@flipster/styled-system/css';
import { center, flex, hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';
import webviewHandler from '@flipster/utils/webview-handler';

import { ImgOrLottie, contentfulImageUrl } from '../../../../../contentful/assets';
import { useAuth } from '../../../../../shared/auth/state';
import { CompetitionIdContext, competitionBunja, competitionTabsBunja } from '../../../state';
import { competitionRankingsBunja } from '../state';
import { rankingStyle } from './ranking.style';

const ListItem = ({ item, index }: { item: Ranking; index: number }) => {
  const id = useContext(CompetitionIdContext);
  const { competitionContentful } = useBunja(competitionBunja);
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);
  const router = useRouter();
  const { user } = useAuth();

  const isRoi = selectedEventTabValue === 'roi';

  const getIconUrl = (rank?: number) => {
    if (!rank) return;

    const url =
      rank === 1
        ? competitionContentful?.rank1Icon?.url
        : rank === 2
          ? competitionContentful?.rank2Icon?.url
          : competitionContentful?.rank3Icon?.url;
    return contentfulImageUrl(url ?? '');
  };

  const isTop3 = item.rank && item.rank <= 3;

  return (
    <li
      key={item.rank}
      data-user-id={item.userId}
      className={css(
        vstack.raw({
          alignItems: 'stretch',
          w: '100%',
          p: 16,
          gap: 8,
          rounded: 8,
        }),
        rankingStyle.raw({ rank: isTop3 ? (item.rank as 1 | 2 | 3) : 'default' }),
        user?.userId === item.userId && {
          borderColor: 'border.default.level-1',
          borderWidth: '1px',
          borderStyle: 'solid',
          bg: 'background.default.level-1',
        },
      )}
    >
      <div className={hstack({ gap: 12 })}>
        {isTop3 ? (
          <ImgOrLottie url={getIconUrl(item.rank)} alt={`rank${item.rank}icon`} css={{ w: 40, h: 40 }} />
        ) : (
          <span
            className={center({
              w: 28,
              h: 28,
              color: 'content.default.level-2',
              textStyle: 'Text/M/Monospace/Strong',
            })}
          >
            {item.rank}
          </span>
        )}
        <button
          type='button'
          className={flex({
            align: 'center',
            gap: 2,
            color: 'content.default.level-5',
            textStyle: 'Text/L/Strong',
          })}
          onClick={() => {
            segment.track(CompetitionsEvent.CompetitionTradingLeaderboardListClick, {
              competition_id: id,
              list_order: index,
              nickname: item.nickname ?? '',
            });
            webviewHandler.postMessage(
              {
                action: 'link',
                params: {
                  page: `user/profile/${item.userProfileId}`,
                },
              },
              () => router.push(`/user/profile/${item.userProfileId}`),
            );
          }}
        >
          <span>{item.nickname ?? 'Nickname'}</span>
          {item.profileVisibility === 'PRIVATE' && <LockClosedIcon size={14} />}
          {item.profileVisibility === 'PUBLIC' && (
            <ChevronRightIcon
              size={14}
              color={isTop3 ? token('colors.inverse.white') : token('colors.content.default.level-1')}
            />
          )}
        </button>
      </div>
      <div className={hstack({ justify: 'space-between' })}>
        <Tooltip.Root>
          <Tooltip.Trigger>
            <p className={css({ textStyle: 'Text/L/Monospace/Regular', color: 'content.accent.VIP' })}>
              <FormattedMessage
                id='Competition.r251218.Est_reward_prize'
                values={{
                  prize: <Formatted.Number value={item.prize ?? 0} css={{ textStyle: 'Text/L/Monospace/Regular' }} />,
                }}
              />
            </p>
          </Tooltip.Trigger>
          <Tooltip.Content bottomSheetTitle={<FormattedMessage id='Competition.r251217.Expected_reward' />} portal>
            <FormattedMessage id='Competition.r251215.Expected_reward_symbol_desc' />
          </Tooltip.Content>
        </Tooltip.Root>
        {isRoi ? (
          <Formatted.Percent
            value={item.value}
            isRate
            toFixed
            withPlus
            withColor
            css={{ textStyle: 'Text/L/Monospace/Regular', color: 'content.default.level-5' }}
          />
        ) : (
          <Formatted.Number
            value={item.value ?? 0}
            toFixed
            precision={2}
            withPlus
            withColor
            css={{ textStyle: 'Text/L/Monospace/Regular', color: 'content.default.level-5' }}
          />
        )}
      </div>
    </li>
  );
};

const ListHeader = () => {
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);
  const isVolume = selectedEventTabValue === 'volume';
  const isRoi = selectedEventTabValue === 'roi';

  return (
    <div
      className={flex({
        justify: 'space-between',
        h: 36,
        pb: 12,
        px: 16,
        color: 'content.default.level-1',
        textStyle: 'Text/M/Regular',
      })}
    >
      <div>
        <span>
          <FormattedMessage id='Common.r251201.Trader' />
        </span>
      </div>
      <div className={hstack()}>
        <span>
          {isVolume ? (
            <FormattedMessage id='Competition.r251215.Volume_symbol' values={{ symbol: UsdtCurrency }} />
          ) : isRoi ? (
            <FormattedMessage id='Tmp.inseok.Roi_symbol' values={{ symbol: '%' }} />
          ) : (
            <FormattedMessage id='Competition.r251218.Pnl_symbol' values={{ symbol: UsdtCurrency }} />
          )}
        </span>
        {isVolume && (
          <Tooltip.Root>
            <Tooltip.Trigger>
              <IconButton hierarchy='quaternary' size='small' icon={<InformationCircleIcon />} />
            </Tooltip.Trigger>
            <Tooltip.Content bottomSheetTitle={<FormattedMessage id='Common.r250923.Volume' />} portal>
              <FormattedMessage id='Competition.r251215.Volume_symbol_desc' />
            </Tooltip.Content>
          </Tooltip.Root>
        )}
      </div>
    </div>
  );
};

export const MobileList = () => {
  const { rankingsAtom } = useBunja(competitionRankingsBunja);
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);
  const rankings = useAtomValue(rankingsAtom);

  if (!rankings) return null;

  return (
    <div
      className={stack({ gap: 8, tablet: { display: 'none' } })}
      id={`leaderboard-table-mobile-${selectedEventTabValue}`}
    >
      <ListHeader />
      {rankings.length > 0 ? (
        <ul className={vstack({ gap: 8 })}>
          {rankings.map((item, index) => (
            <ListItem key={`${item.rank}-${item.nickname}`} item={item} index={index} />
          ))}
        </ul>
      ) : (
        <div
          className={center({
            flexDir: 'column',
            gap: 16,
            p: 40,
            borderColor: 'border.default.level-2',
            borderWidth: '1px',
            borderStyle: 'solid',
            rounded: 16,
            h: { base: 460, tablet: 716 },
          })}
        >
          <p className={css({ textStyle: 'Title/S', textAlign: 'center' })}>
            <FormattedMessage id='Competition.r251218.No_traders_ranked_yet_trade_now_first_one' />
          </p>
        </div>
      )}
    </div>
  );
};
